// 消息通知 —— 浏览器 Notifications API + 铃声 + 频道订阅模式
//
// mode:
//   "all"      = 所有新消息都通知 (默认)
//   "mentions" = 只有 @我 (display_name / username / user.id) 才通知
//   "muted"    = 永不通知

import { getSoundPref, playSound } from "./sounds";

export type RoomNotifyMode = "all" | "mentions" | "muted";

const PREFS_KEY = "chatbox.notify.room-prefs";
type Prefs = Record<string, RoomNotifyMode>;

function readPrefs(): Prefs {
  try {
    const v = localStorage.getItem(PREFS_KEY);
    return v ? (JSON.parse(v) as Prefs) : {};
  } catch {
    return {};
  }
}

function writePrefs(p: Prefs): void {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

export function getRoomNotifyMode(roomId: string): RoomNotifyMode {
  return readPrefs()[roomId] ?? "all";
}

export function setRoomNotifyMode(roomId: string, mode: RoomNotifyMode): void {
  const p = readPrefs();
  if (mode === "all") {
    delete p[roomId]; // default 不存
  } else {
    p[roomId] = mode;
  }
  writePrefs(p);
}

/** 触发通知: 弹原生 Notification + 播铃声 */
export interface NotifyInput {
  roomId: string;
  title: string;
  body: string;
  /** 给 mention 模式判断: 文本中是否含当前用户的名字 */
  mentionedMe?: boolean;
}

export async function maybeNotify(input: NotifyInput): Promise<void> {
  const mode = getRoomNotifyMode(input.roomId);
  if (mode === "muted") return;
  if (mode === "mentions" && !input.mentionedMe) return;

  // 页面在前台就不弹 Notification, 但仍响铃 (UI 已有 in-app 消息流体现)
  if (typeof document !== "undefined" && document.visibilityState === "visible") {
    await playSound(getSoundPref());
    return;
  }
  // 后台 + 有权限 -> Notification
  if (typeof Notification !== "undefined" && Notification.permission === "granted") {
    try {
      const n = new Notification(input.title, {
        body: input.body,
        icon: "/favicon.png?v=2",
        tag: `chatbox-${input.roomId}`,
      });
      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch {
      /* 某些浏览器需要 ServiceWorker 才能 new Notification */
    }
  }
  await playSound(getSoundPref());
}

/** 启动时请求权限 (login 后调一次即可) */
export async function requestNotifyPermission(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

/** 判断一段文本是否提到了当前用户 */
export function textMentions(
  text: string,
  selfNames: { username?: string | null; display_name?: string | null; id?: string },
): boolean {
  const t = text.toLowerCase();
  if (selfNames.username && t.includes(`@${selfNames.username.toLowerCase()}`)) return true;
  if (selfNames.display_name && t.includes(`@${selfNames.display_name.toLowerCase()}`))
    return true;
  if (selfNames.id && t.includes(`@${selfNames.id.toLowerCase()}`)) return true;
  return false;
}
